import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export function Card({
  title,
  action,
  children,
  className = "",
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || action) && (
        <header className="card-header">
          <h2 className="card-title">{title}</h2>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-chalk-400">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-10 text-sm text-chalk-400">
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-field-600 border-t-blitz-500"
        aria-hidden
      />
      {label}…
    </div>
  );
}

/**
 * tRPC reports a failed input check by stringifying the Zod issue array, so
 * `error.message` is a page of JSON rather than a sentence. Pull the messages
 * out; anything else is passed through untouched.
 */
function readableError(error: unknown): string {
  if (!(error instanceof Error)) return "Something went wrong.";
  const raw = error.message;
  // A request that never reached the server fails with the browser's own
  // wording — "Failed to fetch" — which says nothing to a manager on a train.
  // The API answers real failures with a sentence, so a transport error like
  // this one means the connection, not the league.
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(raw)) {
    return typeof navigator !== "undefined" && navigator.onLine === false
      ? "You're offline, so this couldn't load. It'll be here once you have a connection."
      : "Couldn't reach the site — that's usually the connection. Try again in a moment.";
  }
  if (!raw.trimStart().startsWith("[")) return raw;
  try {
    const issues = JSON.parse(raw);
    if (!Array.isArray(issues)) return raw;
    const messages = issues
      .map((i: { message?: unknown }) => (typeof i.message === "string" ? i.message : null))
      .filter((m): m is string => !!m);
    return messages.length ? [...new Set(messages)].join(" ") : raw;
  } catch {
    return raw;
  }
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = readableError(error);
  return (
    <div
      role="alert"
      className="rounded-lg border border-loss-500/40 bg-loss-500/10 px-4 py-3 text-sm text-loss-400"
    >
      {message}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="px-4 py-10 text-center text-sm text-chalk-500">{children}</p>
  );
}

/** W / L / T pill, coloured by result. */
export function ResultBadge({ result }: { result: "W" | "L" | "T" | null }) {
  if (!result) return <span className="text-chalk-500">—</span>;
  const styles = {
    W: "bg-blitz-500/15 text-blitz-400 border-blitz-500/30",
    L: "bg-loss-500/15 text-loss-400 border-loss-500/30",
    T: "bg-flag-500/15 text-flag-400 border-flag-500/30",
  }[result];
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded border text-xs font-bold ${styles}`}
    >
      {result}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    final: ["Final", "border-field-600 bg-field-800 text-chalk-400"],
    scheduled: ["Scheduled", "border-field-600 bg-field-800 text-chalk-500"],
    pending_confirmation: [
      // One half filed, the other still to come.
      "One half in",
      "border-flag-500/40 bg-flag-500/10 text-flag-400",
    ],
    disputed: ["Disputed", "border-loss-500/40 bg-loss-500/10 text-loss-400"],
  };
  const [label, cls] = map[status] ?? [status, "border-field-600 bg-field-800"];
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

export function ManagerLink({
  slug,
  name,
  className = "",
}: {
  slug: string | null;
  name: string | null;
  className?: string;
}) {
  if (!name) return <span className="text-chalk-500">TBD</span>;
  if (!slug) return <span className={className}>{name}</span>;
  return (
    <Link to={`/managers/${slug}`} className={`hover:text-blitz-400 ${className}`}>
      {name}
    </Link>
  );
}

/** A labelled number, for the stat strips at the top of a page. */
export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    // Tighter on a phone: six of these stacked two to a row is the whole first
    // screen otherwise, before any of the page they sit on top of.
    <div className="rounded-lg border border-field-700/60 bg-field-900/60 px-3 py-2 sm:px-4 sm:py-3">
      <div className="text-[10px] font-medium uppercase tracking-wider text-chalk-500 sm:text-[11px]">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-bold tabular-nums sm:mt-1 sm:text-xl">
        {value}
      </div>
      {hint && (
        <div className="mt-0.5 text-[11px] text-chalk-500 sm:text-xs">{hint}</div>
      )}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
}) {
  return (
    <div
      role="tablist"
      className="inline-flex flex-wrap gap-1 rounded-lg border border-field-700 bg-field-900 p-1"
    >
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
            o.value === value
              ? "bg-field-700 text-chalk-50"
              : "text-chalk-400 hover:text-chalk-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function TeamTag({
  abbr,
  ovr,
}: {
  abbr: string | null;
  ovr?: number | null;
}) {
  if (!abbr) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded border border-field-600 bg-field-850 px-1.5 py-0.5 font-mono text-[11px] text-chalk-400">
      {abbr}
      {ovr != null && <span className="text-chalk-500">{ovr}</span>}
    </span>
  );
}

/**
 * A win-loss record. Ties are only shown when there are any — "7-3-0" reads as
 * a different kind of record than "7-3".
 *
 * Not called `Record`: that is TypeScript's own utility type, and shadowing it
 * breaks every `Record<string, T>` in the same file.
 */
export function WinLoss({
  w,
  l,
  t = 0,
  className = "",
}: {
  w: number;
  l: number;
  t?: number;
  className?: string;
}) {
  return (
    <span className={`font-mono font-semibold tabular-nums ${className}`}>
      {t > 0 ? `${w}-${l}-${t}` : `${w}-${l}`}
    </span>
  );
}

/**
 * A signed number coloured by sign — point differential, turnover margin.
 * Four pages each decided this independently; now they can't disagree.
 */
export function Diff({
  value,
  bold = true,
  digits = 0,
}: {
  value: number;
  bold?: boolean;
  /** decimals to show — a per-game margin is not a whole number */
  digits?: number;
}) {
  const tone =
    value > 0 ? "text-blitz-400" : value < 0 ? "text-loss-400" : "text-chalk-400";
  const text = digits > 0 ? value.toFixed(digits) : String(value);
  return (
    <span className={`font-mono tabular-nums ${bold ? "font-semibold" : ""} ${tone}`}>
      {value > 0 ? `+${text}` : text}
    </span>
  );
}

/** A run of results: green while winning, red while losing. */
export function Streak({ value }: { value: string }) {
  const tone = value.startsWith("W")
    ? "text-blitz-400"
    : value.startsWith("L")
      ? "text-loss-400"
      : "text-chalk-500";
  return <span className={`font-mono ${tone}`}>{value}</span>;
}

/** A plain right-aligned number, monospaced so columns line up. */
export function Num({
  value,
  muted = false,
}: {
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <span className={`font-mono tabular-nums ${muted ? "text-chalk-400" : ""}`}>
      {value}
    </span>
  );
}

/**
 * A sortable column header. Pairs with `useSortedRows` below: the table owns
 * the sort state, this renders the control and the current direction.
 */
export function SortableTh<K extends string>({
  sortKey,
  sort,
  onSort,
  align = "left",
  title,
  className = "",
  children,
}: {
  sortKey: K;
  sort: { key: K; dir: "asc" | "desc" };
  onSort: (key: K) => void;
  align?: "left" | "right";
  title?: string;
  /** extra classes on the cell — tighter padding, pinning it in place */
  className?: string;
  children: React.ReactNode;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`th ${align === "right" ? "text-right" : ""} ${className}`}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        title={title}
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-chalk-200 ${
          active ? "text-chalk-100" : ""
        }`}
      >
        {children}
        <span className={active ? "text-blitz-400" : "text-field-600"}>
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "▾"}
        </span>
      </button>
    </th>
  );
}
